import { peerIdMatches } from './peerId';
import type { GpsPeer } from './gpsPeer';
import type { SelfPosition } from './gpsPeer';
import type { ProximityBleBridge } from './bleBridge';

export const BLE_GPS_FRESH_MS = 10_000;
const NOTIFY_INTERVAL_MS = 1000;

interface BleGattGpsPeer {
  userId: string;
  deviceId: string;
  lat: number;
  lng: number;
  accuracyMeters: number;
  lastKnownAt: number;
}

const peersByDeviceId = new Map<string, BleGattGpsPeer>();
const changeListeners = new Set<() => void>();

let latestNotify: SelfPosition | null = null;
let notifyTimer: ReturnType<typeof setTimeout> | null = null;
let lastNotifyAt = 0;

function notifyChange(): void {
  for (const cb of changeListeners) {
    cb();
  }
}

export function onBleGpsPeersChange(cb: () => void): () => void {
  changeListeners.add(cb);
  return () => {
    changeListeners.delete(cb);
  };
}

export function handleBlePeerGpsUpdate(event: {
  userId: string;
  lat: number;
  lng: number;
  accuracyM: number;
  timestampMs: number;
  deviceId: string;
}): void {
  peersByDeviceId.set(event.deviceId, {
    userId: event.userId,
    deviceId: event.deviceId,
    lat: event.lat,
    lng: event.lng,
    accuracyMeters: event.accuracyM,
    lastKnownAt: event.timestampMs,
  });
  notifyChange();
}

export function getBleGpsPeers(): GpsPeer[] {
  return [...peersByDeviceId.values()].map((entry) => ({
    id: entry.userId,
    source: 'ble-gps' as const,
    lat: entry.lat,
    lng: entry.lng,
    accuracyMeters: entry.accuracyMeters,
    headingDegrees: null,
    lastKnownAt: entry.lastKnownAt,
  }));
}

export function clearBleGpsPeers(): void {
  peersByDeviceId.clear();
  notifyChange();
}

function pickCanonicalPeerId(...ids: string[]): string {
  const full = ids.find((id) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id),
  );
  if (full) {
    return full;
  }
  return ids.reduce((best, id) => (id.length > best.length ? id : best), ids[0]!);
}

function pickFresherGps(a: GpsPeer, b: GpsPeer, now: number): GpsPeer {
  const aAge = now - a.lastKnownAt;
  const bAge = now - b.lastKnownAt;
  const aRecent = aAge <= BLE_GPS_FRESH_MS;
  const bRecent = bAge <= BLE_GPS_FRESH_MS;
  if (aRecent && bRecent) {
    if (a.source === 'ble-gps' && b.source !== 'ble-gps') {
      return a;
    }
    if (b.source === 'ble-gps' && a.source !== 'ble-gps') {
      return b;
    }
  }
  return a.lastKnownAt >= b.lastKnownAt ? a : b;
}

/** Merge GPS peer lists; freshest GPS wins, BLE-GPS preferred when both recent (Lynx mergeGpsSources). */
export function mergeGpsSources(peers: GpsPeer[], now = Date.now()): GpsPeer[] {
  const merged: GpsPeer[] = [];
  for (const peer of peers) {
    const idx = merged.findIndex((existing) => peerIdMatches(existing.id, peer.id));
    if (idx < 0) {
      merged.push({ ...peer });
      continue;
    }
    const picked = pickFresherGps(merged[idx]!, peer, now);
    merged[idx] = { ...picked, id: pickCanonicalPeerId(merged[idx]!.id, peer.id) };
  }
  return merged;
}

async function flushGpsNotify(bridge: ProximityBleBridge): Promise<void> {
  notifyTimer = null;
  if (!latestNotify) {
    return;
  }
  const pos = latestNotify;
  latestNotify = null;
  lastNotifyAt = Date.now();
  await bridge.updateGpsForBroadcast({
    lat: pos.lat,
    lng: pos.lng,
    accuracy: Math.min(255, Math.round(pos.accuracyMeters ?? 100)),
    timestampMs: pos.timestampMs,
  });
}

/** Throttled to 1/sec — pushes self GPS to GATT NOTIFY subscribers via native bridge. */
export function notifySelfPositionForBleBroadcast(
  bridge: ProximityBleBridge,
  pos: SelfPosition,
  now = Date.now,
): void {
  latestNotify = pos;
  const elapsed = now() - lastNotifyAt;
  if (elapsed >= NOTIFY_INTERVAL_MS) {
    void flushGpsNotify(bridge);
    return;
  }
  if (notifyTimer === null) {
    notifyTimer = setTimeout(() => {
      void flushGpsNotify(bridge);
    }, NOTIFY_INTERVAL_MS - elapsed);
  }
}

export function stopBleGpsBroadcastTimer(): void {
  if (notifyTimer !== null) {
    clearTimeout(notifyTimer);
    notifyTimer = null;
  }
  latestNotify = null;
}
